/* ──────────────────────────────
   components/CountryTable.jsx
   ────────────────────────────── */

import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import PublicIcon from "@mui/icons-material/Public";

import Pagination from "../../pages/Pagination";
import Male from "../../assets/images/male.png";

import {
  getCountries,
  toggleCountryStatus,
} from "../../store/country/action";

const CountryTable = () => {
  const dispatch = useDispatch();

  /* ───────────── UI State ───────────── */
  const [filter, setFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE
  const [search, setSearch] = useState("");

  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ───────────── Redux ───────────── */
  const { countries = [] } = useSelector((s) => s.country || {});

  /* ───────────── Effects ───────────── */
  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  /* ───────────── Helpers ───────────── */
  const filteredCountries = countries
    .filter((c) => {
      if (filter === "ACTIVE") return c.isActive;
      if (filter === "INACTIVE") return !c.isActive;
      return true;
    })
    .filter((c) =>
      c.name.toLowerCase().includes(search.trim().toLowerCase())
    );

  const paginated = filteredCountries.slice(
    (activePage - 1) * rowsPerPage,
    activePage * rowsPerPage
  );

  const toggleStatus = (id, current) => {
    dispatch(toggleCountryStatus(id, !current));
  };

  /* ───────────── Render ───────────── */
  return (
    <>
      {/* ─── Header ─── */}
      <div className="page-title mb-3">
        <h3 className="text-dark">
          <PublicIcon /> Countries
        </h3>
      </div>

      {/* ─── Filters ─── */}
      <div className="card-header">
        <div className="row align-items-center">
          <div className="col-md-4">
            <select
              className="form-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="ALL">All Countries</option>
              <option value="ACTIVE">Active Only</option>
              <option value="INACTIVE">Inactive Only</option>
            </select>
          </div>

          <div className="col-md-8">
            <input
              type="search"
              className="form-control"
              placeholder="Search country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="card-body pt-0">
        <table className="table table-striped text-center mt-4">
          <thead>
            <tr>
              <th>No.</th>
              <th>Flag</th>
              <th>Country</th>
              <th>Status</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {paginated.map((c, idx) => (
              <tr key={c._id}>
                <td>{idx + 1}</td>

                <td>
                  <img
                    src={c.flag || Male}
                    alt={c.name}
                    height="30"
                    width="45"
                    style={{ objectFit: "cover", borderRadius: 6 }}
                  />
                </td>

                <td>{c.name}</td>

                <td>
                  {c.isActive ? (
                    <span className="badge bg-success">Active</span>
                  ) : (
                    <span className="badge bg-danger">Inactive</span>
                  )}
                </td>

                <td>{dayjs(c.createdAt).format("DD-MMM-YYYY")}</td>

                <td>
                  <Tooltip title="Toggle Status">
                    <button
                      className={`btn btn-sm ${
                        c.isActive ? "btn-success" : "btn-secondary"
                      }`}
                      onClick={() => toggleStatus(c._id, c.isActive)}
                    >
                      {c.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}

            {filteredCountries.length === 0 && (
              <tr>
                <td colSpan="6">Nothing to show</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ─── Pagination ─── */}
        <Pagination
          activePage={activePage}
          rowsPerPage={rowsPerPage}
          userTotal={filteredCountries.length}
          handleRowsPerPage={setRowsPerPage}
          handlePageChange={setActivePage}
        />
      </div>
    </>
  );
};

export default CountryTable;
